export type DocumentAutoImportBlocker =
  | 'not_ddt'
  | 'document_incomplete'
  | 'estimated_total'
  | 'low_confidence'
  | 'supplier_not_matched'
  | 'duplicate'
  | 'possible_missing_page'
  | 'no_lines'
  | 'code_not_matched'
  | 'line_gate'
  | 'invalid_quantity'
  | 'uom_not_deterministic'
  | 'lot_or_expiry_incomplete'
  | 'economics_incoherent'
  | 'similar_lines';

export interface DocumentAutoImportAssessment {
  eligible: boolean;
  blockers: DocumentAutoImportBlocker[];
}

interface AutoImportLineLike {
  parsed?: {
    supplier_code?: unknown;
    qty?: unknown;
    uom?: unknown;
    uom_canonical?: unknown;
    uom_recognized?: unknown;
    total_weight_kg?: unknown;
    unit_price?: unknown;
    line_total?: unknown;
    batch_lot?: unknown;
    expiration_date?: unknown;
    similar_to?: unknown;
  };
  food_match?: { id?: unknown; default_uom?: unknown } | null;
  match_source?: unknown;
  validation?: { status?: unknown; reasons?: unknown } | null;
}

interface AutoImportResultLike {
  confidence?: unknown;
  document?: {
    type?: unknown;
    number?: unknown;
    date?: unknown;
    total_amount_estimated?: unknown;
  };
  supplier?: { existing?: { id?: unknown } | null };
  lines?: unknown;
  existing_document?: unknown;
  possible_missing_page?: unknown;
}

function positiveFinite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function nonNegativeFinite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizedUom(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase().replace(/\.+$/, '')
    : '';
}

function validIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

/**
 * T-553 — fail-closed gate for a genuinely unattended DDT import.
 *
 * A historical supplier-code match is necessary but not sufficient: the AI
 * extraction must also be complete, internally coherent and free of every
 * review gate. Older dry-runs that do not carry the required evidence are
 * deliberately sent to manual review.
 */
export function assessDocumentAutoImport(
  input: unknown,
): DocumentAutoImportAssessment {
  const raw = input as AutoImportResultLike | null | undefined;
  const blockers = new Set<DocumentAutoImportBlocker>();
  if (!raw || raw.document?.type !== 'ddt') blockers.add('not_ddt');
  if (!nonEmpty(raw?.document?.number) || !validIsoDate(raw?.document?.date)) {
    blockers.add('document_incomplete');
  }
  if (raw?.document?.total_amount_estimated === true) blockers.add('estimated_total');
  if (!positiveFinite(raw?.confidence) || raw.confidence < 0.8) blockers.add('low_confidence');
  if (!nonEmpty(raw?.supplier?.existing?.id)) blockers.add('supplier_not_matched');
  if (raw?.existing_document) blockers.add('duplicate');
  if (raw?.possible_missing_page === true) blockers.add('possible_missing_page');

  const lines = Array.isArray(raw?.lines) ? raw.lines as AutoImportLineLike[] : [];
  if (lines.length === 0) blockers.add('no_lines');
  const codes = new Set<string>();
  for (const line of lines) {
    const parsed = line.parsed ?? {};
    const code = nonEmpty(parsed.supplier_code)
      ? parsed.supplier_code.trim().toLowerCase()
      : '';
    if (
      !code
      || codes.has(code)
      || line.match_source !== 'code'
      || !nonEmpty(line.food_match?.id)
    ) blockers.add('code_not_matched');
    if (code) codes.add(code);

    if (
      line.validation?.status !== 'ok'
      || !Array.isArray(line.validation.reasons)
      || line.validation.reasons.length > 0
    ) blockers.add('line_gate');

    const stockQty = positiveFinite(parsed.total_weight_kg)
      ? parsed.total_weight_kg
      : parsed.qty;
    if (!positiveFinite(stockQty)) blockers.add('invalid_quantity');
    if (
      positiveFinite(parsed.total_weight_kg)
      && positiveFinite(parsed.qty)
      && Math.abs(parsed.total_weight_kg - parsed.qty) > 0.000001
    ) blockers.add('invalid_quantity');

    const sourceUom = normalizedUom(parsed.uom_canonical ?? parsed.uom);
    const stockUom = normalizedUom(line.food_match?.default_uom);
    if (parsed.uom_recognized !== true || !sourceUom || sourceUom !== stockUom) {
      blockers.add('uom_not_deterministic');
    }

    if (!nonEmpty(parsed.batch_lot) || !validIsoDate(parsed.expiration_date)) {
      blockers.add('lot_or_expiry_incomplete');
    } else if (
      validIsoDate(raw?.document?.date)
      && parsed.expiration_date < raw.document.date
    ) {
      blockers.add('lot_or_expiry_incomplete');
    }

    if (!nonNegativeFinite(parsed.unit_price) || !nonNegativeFinite(parsed.line_total)) {
      blockers.add('economics_incoherent');
    } else if (positiveFinite(parsed.qty)) {
      const expected = parsed.qty * parsed.unit_price;
      const tolerance = Math.max(0.02, Math.abs(parsed.line_total) * 0.005);
      if (Math.abs(expected - parsed.line_total) > tolerance) {
        blockers.add('economics_incoherent');
      }
    }

    if (Array.isArray(parsed.similar_to) && parsed.similar_to.length > 0) {
      blockers.add('similar_lines');
    }
  }
  return { eligible: blockers.size === 0, blockers: Array.from(blockers) };
}
